<template>
  <div class="space-y-8">
    <!-- File ID & End Date -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <DocumentTextIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Dossier") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.fileId" :label="$gettext('Numéro de dossier')" />
          <ExtractionInput v-model="data.endDate" type="date" :label="$gettext('Date de fin')" />
          <ExtractionInput v-model="data.commission" type="number" :label="$gettext('Commission')" />
          <ExtractionInput v-model="data.commissionTypeId" type="select" :label="$gettext('Type de commission')" :options="commissionTypeOptions" />
        </div>
      </div>
    </div>

    <!-- Sellers -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <UserGroupIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Vendeurs") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.sellers.length }}
        </span>
      </div>

      <div v-for="(seller, index) in data.sellers" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">

        <!-- ===== Company seller card ===== -->
        <template v-if="seller.type === 'company'">
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedSellers[index] = !expandedSellers[index]"
            >
              <div class="flex items-center gap-3">
                <BriefcaseIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Société") }} {{ index + 1 }}
                  <span v-if="seller.companyName" class="text-gray-500">— {{ seller.companyName }}</span>
                </span>
                <span v-if="seller.matchedCompanyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existante") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créée") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedSellers[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeSeller(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedSellers[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="seller.companyName" :label="$gettext('Dénomination sociale')" required :error="!seller.matchedCompanyId && !seller.companyName?.trim()" />
              <ExtractionInput v-model="seller.companyNumber" :label="$gettext('Numéro d\'entreprise')" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="seller.companyAddress.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!seller.matchedCompanyId && !seller.companyAddress.street?.trim()" @address-selected="(a) => { seller.companyAddress.city = a.city; seller.companyAddress.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="seller.companyAddress.zipcode" :placeholder="$gettext('Code postal')" required :error="!seller.matchedCompanyId && !seller.companyAddress.zipcode?.trim()" />
                  <ExtractionInput v-model="seller.companyAddress.city" :placeholder="$gettext('Ville')" required :error="!seller.matchedCompanyId && !seller.companyAddress.city?.trim()" />
                </div>
              </div>
              <!-- Required fields dropdowns (only for new companies) -->
              <template v-if="!seller.matchedCompanyId">
                <ExtractionInput v-model="seller.companyTypeId" type="select" :label="$gettext('Forme juridique')" required :error="!seller.companyTypeId" :options="companyTypeOptions" />
                <ExtractionInput v-model="seller.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!seller.industrySectorId" :options="industrySectorOptions" />
              </template>
            </div>

            <!-- Signatory sub-section -->
            <div v-if="seller.representative" class="mt-4 pt-4 border-t border-gray-200">
              <h5 class="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{{ $gettext("Signataire") }}</h5>
              <div class="grid grid-cols-2 gap-3">
                <ExtractionInput v-model="seller.representative.lastName" :label="$gettext('Nom')" />
                <ExtractionInput v-model="seller.representative.firstName" :label="$gettext('Prénom')" />
                <ExtractionInput v-model="seller.representative.role" :label="$gettext('Fonction')" />
                <ExtractionInput v-model="seller.representative.nationalNumber" :label="$gettext('Numéro national')" />
                <ExtractionInput v-model="seller.representative.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!seller.representative.matchedPhysicalPersonId && (seller.representative.firstName?.trim() || seller.representative.lastName?.trim()) && !seller.representative.birthdate" />
                <ExtractionInput v-model="seller.representative.birthCity" :label="$gettext('Lieu de naissance')" />
                <ExtractionInput v-model="seller.representative.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!seller.representative.matchedPhysicalPersonId && (seller.representative.firstName?.trim() || seller.representative.lastName?.trim()) && !seller.representative.nationalityCountryId" />
                <div class="col-span-2">
                  <ExtractionAddressAutocomplete v-model="seller.representative.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!seller.representative.matchedPhysicalPersonId && !seller.representative.address.street?.trim()" @address-selected="(a) => { seller.representative.address.city = a.city; seller.representative.address.zipcode = a.zipcode; }" />
                  <div class="grid grid-cols-2 gap-2 mt-1">
                    <ExtractionInput v-model="seller.representative.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!seller.representative.matchedPhysicalPersonId && !seller.representative.address.zipcode?.trim()" />
                    <ExtractionInput v-model="seller.representative.address.city" :placeholder="$gettext('Ville')" required :error="!seller.representative.matchedPhysicalPersonId && !seller.representative.address.city?.trim()" />
                  </div>
                </div>
                <!-- PEP questions for representative (only for new physical persons) -->
                <template v-if="!seller.representative.matchedPhysicalPersonId">
                  <ExtractionInput v-model="seller.representative.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="seller.representative.isPoliticalyExposed === null" />
                  <ExtractionInput v-model="seller.representative.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="seller.representative.isCloseToPoliticalyExposed === null" />
                  <ExtractionInput v-model="seller.representative.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!seller.representative.identificationType" :options="identificationTypeOptions" />
                  <ExtractionInput v-model="seller.representative.professionId" type="select" :label="$gettext('Profession')" required :error="!seller.representative.professionId" :options="professionOptions" />
                  <ExtractionInput v-model="seller.representative.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!seller.representative.industrySectorId" :options="industrySectorOptions" />
                </template>
                <!-- ID document upload for representative -->
                <IdentityDocumentUpload
                  v-if="showIdentityExtraction"
                  :person="seller"
                  :person-index="index"
                  :is-extracting="extractingIdentityIndex === index"
                  :identity-error="identityErrorIndex === index ? identityError : null"
                  :label="$gettext('Documents d\'identité du signataire')"
                  @add-file="({ event }) => onIdentityFileAdd(event, index)"
                  @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                  @extract="() => onExtractIdentity(index)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ===== Physical person seller card ===== -->
        <template v-else>
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedSellers[index] = !expandedSellers[index]"
            >
              <div class="flex items-center gap-3">
                <UserIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Vendeur") }} {{ index + 1 }}
                  <span v-if="seller.firstName || seller.lastName" class="text-gray-500">— {{ [seller.firstName, seller.lastName].filter(Boolean).join(' ') }}</span>
                </span>
                <span v-if="seller.matchConfidence === 'high'" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existant") }}
                </span>
                <span v-else-if="seller.matchConfidence === 'medium'" class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {{ $gettext("Correspondance partielle") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créé") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedSellers[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeSeller(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedSellers[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="seller.lastName" :label="$gettext('Nom')" />
              <ExtractionInput v-model="seller.firstName" :label="$gettext('Prénom')" />
              <ExtractionInput v-model="seller.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!seller.matchedPhysicalPersonId && (seller.firstName?.trim() || seller.lastName?.trim()) && !seller.birthdate" />
              <ExtractionInput v-model="seller.birthCity" :label="$gettext('Lieu de naissance')" />
              <ExtractionInput v-model="seller.nationalNumber" :label="$gettext('Numéro national')" />
              <ExtractionInput v-model="seller.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!seller.matchedPhysicalPersonId && (seller.firstName?.trim() || seller.lastName?.trim()) && !seller.nationalityCountryId" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="seller.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!seller.matchedPhysicalPersonId && !seller.address.street?.trim()" @address-selected="(a) => { seller.address.city = a.city; seller.address.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="seller.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!seller.matchedPhysicalPersonId && !seller.address.zipcode?.trim()" />
                  <ExtractionInput v-model="seller.address.city" :placeholder="$gettext('Ville')" required :error="!seller.matchedPhysicalPersonId && !seller.address.city?.trim()" />
                </div>
              </div>
              <!-- Required fields (only for new physical persons) -->
              <template v-if="!seller.matchedPhysicalPersonId">
                <ExtractionInput v-model="seller.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="seller.isPoliticalyExposed === null" />
                <ExtractionInput v-model="seller.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="seller.isCloseToPoliticalyExposed === null" />
                <ExtractionInput v-model="seller.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!seller.identificationType" :options="identificationTypeOptions" />
                <ExtractionInput v-model="seller.professionId" type="select" :label="$gettext('Profession')" required :error="!seller.professionId" :options="professionOptions" />
                <ExtractionInput v-model="seller.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!seller.industrySectorId" :options="industrySectorOptions" />
              </template>
              <!-- ID document upload -->
              <IdentityDocumentUpload
                v-if="showIdentityExtraction"
                :person="seller"
                :person-index="index"
                :is-extracting="extractingIdentityIndex === index"
                :identity-error="identityErrorIndex === index ? identityError : null"
                @add-file="({ event }) => onIdentityFileAdd(event, index)"
                @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                @extract="() => onExtractIdentity(index)"
              />
            </div>
          </div>
        </template>

      </div>
      <p v-if="!isValid" class="mt-2 text-xs text-red-600">
        {{ $gettext("Veuillez remplir tous les champs obligatoires (*) avant de confirmer.") }}
      </p>
    </div>

    <!-- Properties -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <HomeIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Propriétés") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.properties.length }}
        </span>
      </div>

      <div v-for="(property, index) in data.properties" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">
        <!-- Collapsible header -->
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50/80 hover:bg-gray-100/80 text-left transition-colors duration-150"
          @click="expandedProperties[index] = !expandedProperties[index]"
        >
          <div class="flex items-center gap-3">
            <HomeModernIcon class="h-4 w-4 text-gray-400 shrink-0" />
            <span class="text-sm font-medium text-gray-700">
              {{ $gettext("Propriété") }} {{ index + 1 }}
              <span v-if="property.address?.street" class="text-gray-500">— {{ property.address.street }}, {{ property.address.city }}</span>
            </span>
            <span v-if="property.matchedPropertyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              {{ $gettext("Existante") }}
            </span>
            <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
              {{ $gettext("Sera créée") }}
            </span>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedProperties[index] ? 'rotate-180' : ''" />
        </button>

        <!-- Card body -->
        <div v-show="expandedProperties[index]" class="p-4">
          <!-- Info banner for matched properties: edits will apply to the existing record -->
          <div
            v-if="property.matchedPropertyId && !property.forceCreate"
            class="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
          >
            <InformationCircleIcon class="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div class="flex-1 text-xs text-amber-800">
              <p class="font-medium">{{ $gettext("Ce bien existe déjà dans votre base") }}</p>
              <p class="mt-0.5">{{ $gettext("Vos modifications ci-dessous seront appliquées au bien existant. Si vous souhaitez créer un bien distinct (par exemple un autre garage à la même adresse), cochez la case ci-dessous.") }}</p>
              <p v-if="takenUnitLabels(property).length" class="mt-1.5">
                {{ $gettext("Numéros déjà utilisés à cette adresse :") }}
                <span class="font-semibold">{{ takenUnitLabels(property).join(", ") }}</span>
              </p>
              <label class="mt-2 inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="property.forceCreate" class="rounded border-amber-300 text-indigo-600 focus:ring-indigo-500" />
                <span class="text-amber-900">{{ $gettext("Forcer la création d'un nouveau bien") }}</span>
              </label>
            </div>
          </div>
          <div v-if="property.forceCreate" class="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-2">
            <InformationCircleIcon class="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
            <div class="flex-1 text-xs text-indigo-800">
              <p>{{ $gettext("Un nouveau bien sera créé avec les valeurs saisies ci-dessous, indépendamment du bien existant à la même adresse.") }}</p>
              <p v-if="takenUnitLabels(property).length" class="mt-1.5">
                {{ $gettext("Numéros déjà utilisés à cette adresse :") }}
                <span class="font-semibold">{{ takenUnitLabels(property).join(", ") }}</span>
                — {{ $gettext("choisissez un numéro différent.") }}
              </p>
            </div>
          </div>
          <!-- Non-matched property: still warn if other properties exist at this address -->
          <div
            v-if="!property.matchedPropertyId && takenUnitLabels(property).length"
            class="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
          >
            <InformationCircleIcon class="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <p class="text-xs text-amber-800">
              {{ $gettext("D'autres biens existent déjà à cette adresse avec les numéros :") }}
              <span class="font-semibold">{{ takenUnitLabels(property).join(", ") }}</span>.
              {{ $gettext("Choisissez un numéro différent pour ce nouveau bien.") }}
            </p>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2">
              <ExtractionAddressAutocomplete v-model="property.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!effectiveMatchedId(property) && !property.address.street?.trim()" @address-selected="(a) => { property.address.city = a.city; property.address.zipcode = a.zipcode; }" />
              <div class="grid grid-cols-3 gap-2 mt-1">
                <ExtractionInput
                  v-model="property.address.apartmentNumber"
                  :placeholder="apartmentNumberLabel(property)"
                  :required="isUnitIdentifierRequired(property)"
                  :error="(isUnitIdentifierRequired(property) && !property.address.apartmentNumber?.trim()) || isApartmentNumberColliding(property)"
                />
                <ExtractionInput v-model="property.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!effectiveMatchedId(property) && !property.address.zipcode?.trim()" />
                <ExtractionInput v-model="property.address.city" :placeholder="$gettext('Ville')" required :error="!effectiveMatchedId(property) && !property.address.city?.trim()" />
              </div>
              <p v-if="isApartmentNumberColliding(property)" class="mt-1 text-xs text-red-600">
                {{ $gettext("Ce numéro est déjà utilisé par un autre bien à la même adresse. Choisissez un numéro différent.") }}
              </p>
            </div>
            <ExtractionInput
              v-model="property.propertyTypeId"
              type="select"
              :label="$gettext('Type de bien')"
              :options="propertyTypeOptions"
              required
              :error="!effectiveMatchedId(property) && !property.propertyTypeId"
            />
            <ExtractionInput v-model="property.listingPrice" type="number" :label="$gettext('Prix de mise en vente')" />
            <ExtractionInput v-model="property.surfaceArea" type="number" :label="$gettext('Superficie (m²)')" :min="0" :error="!!property.surfaceArea && property.surfaceArea < 0" />
            <ExtractionInput v-model="property.details" :label="$gettext('Détails')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, toRaw } from "vue";
import { DocumentTextIcon, UserGroupIcon, HomeIcon, HomeModernIcon, BriefcaseIcon, UserIcon, ChevronDownIcon, TrashIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";
import { useIndustrySectorStore } from "@/stores/industrySector";
import { useProfessionStore } from "@/stores/profession";
import { useCompanyTypeStore } from "@/stores/companyType";
import { useCountryStore } from "@/stores/country";
import { usePropertyTypeStore } from "@/stores/propertyType";
import { useCommissionTypeStore } from "@/stores/commissionType";
import IdentityDocumentUpload from "@/components/ui/IdentityDocumentUpload.vue";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";
import ExtractionAddressAutocomplete from "@/components/ui/ExtractionAddressAutocomplete.vue";

const props = defineProps({
  extractionData: { type: Object, required: true },
  showIdentityExtraction: { type: Boolean, default: true }
});

const emit = defineEmits(["extract-identity"]);

const industrySectorStore = useIndustrySectorStore();
const professionStore = useProfessionStore();
const companyTypeStore = useCompanyTypeStore();
const countryStore = useCountryStore();
const propertyTypeStore = usePropertyTypeStore();
const commissionTypeStore = useCommissionTypeStore();

// Fetch Belgian company types when countries are loaded
watch(() => countryStore.sortedCountries, (countries) => {
  if (!countries?.length) return;
  const belgium = countries.find(c => c.attributes.code === "BE");
  if (belgium) companyTypeStore.fetchCompanyTypes(belgium.id);
}, { immediate: true });

const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map(is => ({ value: is.id, label: is.attributes.name }))
);
const professionOptions = computed(() =>
  professionStore.professions.map(p => ({ value: p.id, label: p.attributes.name }))
);
const companyTypeOptions = computed(() =>
  companyTypeStore.companyTypes.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);
const nationalityCountryOptions = computed(() =>
  countryStore.sortedCountries.map(c => ({ value: c.id, label: c.attributes.demonym || c.attributes.name }))
);
const propertyTypeOptions = computed(() =>
  propertyTypeStore.sortedPropertyTypes.map(pt => ({ value: pt.id, label: pt.attributes.name }))
);
const commissionTypeOptions = computed(() =>
  commissionTypeStore.all.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);

const propertyTypeById = (id) =>
  propertyTypeStore.all.find(pt => pt.id === id);

// Returns the matchedPropertyId that should effectively be used at /apply time.
// If the user checked "force create a new property", the match is discarded —
// which also means required-field errors must trigger (new property branch).
const effectiveMatchedId = (property) =>
  property?.forceCreate ? null : property?.matchedPropertyId;

// Backend-provided list of existing Properties at the same address.
// Shape: [{ propertyId, apartmentNumber, propertyTypeName }, ...]
const existingUnitsFor = (property) =>
  Array.isArray(property?.existingUnitsAtAddress) ? property.existingUnitsAtAddress : [];

// Human-friendly labels for the taken units, optionally excluding the one
// currently matched (so the user editing an existing bien doesn't see its own
// number listed as "taken").
const takenUnitLabels = (property, { excludeSelf = false } = {}) => {
  const matchedId = property?.matchedPropertyId;
  return existingUnitsFor(property)
    .filter(u => !(excludeSelf && matchedId && u.propertyId === matchedId))
    .filter(u => u.apartmentNumber?.trim())
    .map(u => (u.propertyTypeName ? `${u.apartmentNumber} (${u.propertyTypeName})` : u.apartmentNumber));
};

// Returns true if the apt number currently entered collides with another
// existing Property at this address (excluding the matched property itself
// when the user is editing it, i.e. not force-creating).
const isApartmentNumberColliding = (property) => {
  const apt = property?.address?.apartmentNumber?.trim();
  if (!apt) return false;
  const normalized = apt.toLowerCase();
  const matchedId = property?.matchedPropertyId;
  const isEditingMatched = matchedId && !property?.forceCreate;
  return existingUnitsFor(property).some((u) => {
    if (isEditingMatched && u.propertyId === matchedId) return false;
    return u.apartmentNumber?.trim().toLowerCase() === normalized;
  });
};

const isUnitIdentifierRequired = (property) =>
  propertyTypeById(property?.propertyTypeId)?.attributes?.requiresUnitIdentifier === true;

const apartmentNumberLabel = (property) => {
  const name = propertyTypeById(property?.propertyTypeId)?.attributes?.name;
  if (name === "Garage") return "N° garage";
  if (name === "Parking") return "N° parking";
  if (name === "Cave") return "N° cave";
  if (name === "Bureau") return "N° bureau";
  return "Appart.";
};
const identificationTypeOptions = [
  { value: "faceToFace", label: "Face à face" },
  { value: "remote", label: "A distance" },
  { value: "none", label: "Aucun" }
];

const extractingIdentityIndex = ref(null);
const identityError = ref(null);
const identityErrorIndex = ref(null);

const data = reactive({
  fileId: props.extractionData.fileId || "",
  endDate: props.extractionData.endDate || "",
  commission: props.extractionData.commission ?? null,
  commissionTypeId: props.extractionData.commissionType?.id || null,
  sellers: (props.extractionData.sellers || []).map(s => ({
    ...s,
    type: s.type || "physicalPerson",
    identityFiles: [],
    identityExtracted: false,
    companyName: s.companyName || "",
    companyNumber: s.companyNumber || "",
    companyTypeId: s.companyTypeId || null,
    industrySectorId: s.industrySectorId || null,
    companyAddress: {
      street: s.companyAddress?.street || "",
      city: s.companyAddress?.city || "",
      zipcode: s.companyAddress?.zipcode || ""
    },
    representative: s.representative ? {
      ...s.representative,
      role: s.representative.role || "",
      birthCity: s.representative.birthCity || "",
      isPoliticalyExposed: s.representative.isPoliticalyExposed ?? null,
      isCloseToPoliticalyExposed: s.representative.isCloseToPoliticalyExposed ?? null,
      identificationType: s.representative.identificationType || null,
      professionId: s.representative.professionId || null,
      nationalityCountryId: s.representative.nationalityCountryId || null,
      industrySectorId: s.representative.industrySectorId || null,
      address: {
        street: s.representative.address?.street || "",
        city: s.representative.address?.city || "",
        zipcode: s.representative.address?.zipcode || ""
      }
    } : null,
    isPoliticalyExposed: s.isPoliticalyExposed ?? null,
    isCloseToPoliticalyExposed: s.isCloseToPoliticalyExposed ?? null,
    identificationType: s.identificationType || null,
    professionId: s.professionId || null,
    nationalityCountryId: s.nationalityCountryId || null,
    birthCity: s.birthCity || "",
    address: { street: s.address?.street || "", city: s.address?.city || "", zipcode: s.address?.zipcode || "" }
  })),
  properties: (props.extractionData.properties || []).map(p => ({
    ...p,
    surfaceArea: p.surfaceArea || null,
    propertyTypeId: p.propertyTypeId || null,
    forceCreate: false,
    existingUnitsAtAddress: Array.isArray(p.existingUnitsAtAddress) ? p.existingUnitsAtAddress : [],
    address: {
      street: p.address?.street || "",
      city: p.address?.city || "",
      zipcode: p.address?.zipcode || "",
      apartmentNumber: p.address?.apartmentNumber || ""
    }
  }))
});

// Collapsible state — all expanded by default
const expandedSellers = ref(data.sellers.map(() => true));
const expandedProperties = ref(data.properties.map(() => true));

const removeSeller = (index) => {
  data.sellers.splice(index, 1);
  expandedSellers.value.splice(index, 1);
};

const isValid = computed(() => {
  const sellersValid = data.sellers.every(seller => {
    if (seller.type === "company") {
      if (!seller.matchedCompanyId) {
        if (!seller.companyName?.trim()) return false;
        if (!seller.companyAddress.street?.trim()) return false;
        if (!seller.companyAddress.zipcode?.trim()) return false;
        if (!seller.companyAddress.city?.trim()) return false;
        if (!seller.companyTypeId) return false;
        if (!seller.industrySectorId) return false;
      }
      if (seller.representative && !seller.representative.matchedPhysicalPersonId) {
        if (!seller.representative.firstName?.trim() && !seller.representative.lastName?.trim()) return false;
        if (seller.representative.firstName?.trim() || seller.representative.lastName?.trim()) {
          if (!seller.representative.birthdate) return false;
          if (!seller.representative.nationalityCountryId) return false;
        }
        if (!seller.representative.address.street?.trim()) return false;
        if (!seller.representative.address.zipcode?.trim()) return false;
        if (!seller.representative.address.city?.trim()) return false;
        if (seller.representative.isPoliticalyExposed === null) return false;
        if (seller.representative.isCloseToPoliticalyExposed === null) return false;
        if (!seller.representative.identificationType) return false;
        if (!seller.representative.professionId) return false;
        if (!seller.representative.industrySectorId) return false;
      }
      return true;
    } else {
      if (seller.matchedPhysicalPersonId) return true;
      if (!seller.firstName?.trim() && !seller.lastName?.trim()) return true;
      if (!seller.birthdate) return false;
      if (!seller.nationalityCountryId) return false;
      if (!seller.address.street?.trim()) return false;
      if (!seller.address.zipcode?.trim()) return false;
      if (!seller.address.city?.trim()) return false;
      if (seller.isPoliticalyExposed === null) return false;
      if (seller.isCloseToPoliticalyExposed === null) return false;
      if (!seller.identificationType) return false;
      if (!seller.professionId) return false;
      if (!seller.industrySectorId) return false;
      return true;
    }
  });

  const propertiesValid = data.properties.every(property => {
    // Even when matched, the apartment_number is still required by the backend
    // if the type requires a unit identifier — don't short-circuit on matched.
    const matched = effectiveMatchedId(property);
    if (!matched) {
      if (!property.address.street?.trim()) return false;
      if (!property.address.zipcode?.trim()) return false;
      if (!property.address.city?.trim()) return false;
      if (property.surfaceArea != null && property.surfaceArea < 0) return false;
      if (!property.propertyTypeId) return false;
    }
    if (isUnitIdentifierRequired(property) && !property.address.apartmentNumber?.trim()) return false;
    if (isApartmentNumberColliding(property)) return false;
    return true;
  });

  return sellersValid && propertiesValid;
});

const onIdentityFileAdd = (event, sellerIndex) => {
  const file = event.target.files[0];
  if (!file) return;
  event.target.value = "";

  const seller = data.sellers[sellerIndex];
  if (!seller || seller.identityFiles.length >= 2) return;

  seller.identityFiles.push(file);
  seller.identityExtracted = false;
  identityError.value = null;
  identityErrorIndex.value = null;
};

const removeIdentityFile = (sellerIndex, fileIndex) => {
  const seller = data.sellers[sellerIndex];
  if (!seller) return;
  seller.identityFiles.splice(fileIndex, 1);
  seller.identityExtracted = false;
};

const onExtractIdentity = (sellerIndex) => {
  const seller = data.sellers[sellerIndex];
  if (!seller || seller.identityFiles.length === 0) return;

  identityError.value = null;
  identityErrorIndex.value = null;
  extractingIdentityIndex.value = sellerIndex;

  emit("extract-identity", { files: seller.identityFiles.map(f => toRaw(f)), sellerIndex });
};

const mergeIdentityData = (sellerIndex, identityData) => {
  extractingIdentityIndex.value = null;
  const seller = data.sellers[sellerIndex];
  if (!seller) return;

  seller.identityExtracted = true;

  const target = seller.type === "company" && seller.representative ? seller.representative : seller;

  if (identityData.lastName) target.lastName = identityData.lastName;
  if (identityData.firstName) target.firstName = identityData.firstName;
  if (identityData.birthdate) target.birthdate = identityData.birthdate;
  if (identityData.birthCity) target.birthCity = identityData.birthCity;
  if (identityData.nationalNumber) target.nationalNumber = identityData.nationalNumber;
  if (identityData.nationalityCountryId) target.nationalityCountryId = identityData.nationalityCountryId;
  if (identityData.address?.street) target.address.street = identityData.address.street;
  if (identityData.address?.city) target.address.city = identityData.address.city;
  if (identityData.address?.zipcode) target.address.zipcode = identityData.address.zipcode;

  if (identityData.matchedPhysicalPersonId) {
    target.matchedPhysicalPersonId = identityData.matchedPhysicalPersonId;
    target.matchedPhysicalPersonName = identityData.matchedPhysicalPersonName;
    target.matchConfidence = identityData.matchConfidence;
  }
};

const onIdentityError = (sellerIndex, message) => {
  extractingIdentityIndex.value = null;
  identityError.value = message;
  identityErrorIndex.value = sellerIndex;
};

const getReviewedData = () => {
  return {
    fileId: data.fileId,
    endDate: data.endDate,
    commission: data.commission,
    commissionType: data.commissionTypeId ? { id: data.commissionTypeId } : null,
    sellers: data.sellers,
    properties: data.properties.map((p) => {
      // If the user opted to create a new property despite a match, strip the
      // matchedPropertyId so the backend falls into the `create!` branch.
      const copy = { ...p };
      delete copy.forceCreate;
      if (p.forceCreate) {
        delete copy.matchedPropertyId;
        delete copy.matchedPropertyAddress;
        delete copy.matchConfidence;
      }
      return copy;
    })
  };
};

const getIdentityFiles = () => {
  return data.sellers.flatMap(s => (s.identityFiles || []).map(f => toRaw(f)));
};

const getIdentityFilesPerSeller = () => {
  return data.sellers.map((s, idx) => ({
    sellerIndex: idx,
    sellerType: s.type,
    files: (s.identityFiles || []).map(f => toRaw(f))
  }));
};

defineExpose({ getReviewedData, mergeIdentityData, onIdentityError, isValid, getIdentityFiles, getIdentityFilesPerSeller });
</script>
